import React, { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { useIntl } from '@atlassian/jira-intl';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous';
import type { State } from '../../../../common/types';
import type { ResultUseMatches } from '../../../../common/types/use-matches';
import { CommandPaletteScreenReaderContent } from '../../../../common/ui/command-palette-search-dropdown/command-palette-screen-reader-content';
import { useCommandPalette } from '../../../../controllers';
import messages from './messages';

export const ScreenReaderContent = ({
	activeCommand,
	results,
}: Pick<ResultUseMatches, 'activeCommand' | 'results'>) => {
	const [{ search, activeIndex }, { forceFocusSearch }] = useCommandPalette();
	const previousSearch = usePrevious(search);

	const { getScreenReaderText } = useGetScreenReaderText();
	const [screenReaderText, setScreenReaderText] = useState<string | null>(null);

	const debouncedSetScreenReaderText = useMemo(() => debounce(setScreenReaderText, 400), []);

	useEffect(
		() => () => {
			debouncedSetScreenReaderText?.cancel?.();
		},
		[debouncedSetScreenReaderText],
	);

	useEffect(() => {
		if (previousSearch !== search) {
			debouncedSetScreenReaderText(getScreenReaderText({ activeCommand, results, activeIndex }));
		}
	}, [
		activeCommand,
		getScreenReaderText,
		previousSearch,
		results,
		search,
		activeIndex,
		debouncedSetScreenReaderText,
	]);

	const onFocus = useCallback(() => {
		forceFocusSearch(true);
	}, [forceFocusSearch]);

	return (
		<CommandPaletteScreenReaderContent
			data-testid="command-palette.ui.modal.modal-content.screen-reader-content.command-palette-screen-reader-content"
			onFocus={onFocus}
		>
			{screenReaderText}
		</CommandPaletteScreenReaderContent>
	);
};

export const useGetScreenReaderText = () => {
	const { formatMessage } = useIntl();
	const getScreenReaderText = ({
		activeCommand,
		results,
		activeIndex,
	}: Pick<ResultUseMatches, 'activeCommand' | 'results'> & Pick<State, 'activeIndex'>) => {
		if (!activeCommand) {
			return null;
		}
		const selectedOptionLabel = activeCommand?.name;
		const selectedIndex = activeIndex + 1;
		const totalSuggestions = results.filter((result) => !result.isGroup).length;

		return formatMessage(messages.selectedOption, {
			selectedOptionLabel,
			selectedIndex,
			totalSuggestions,
		});
	};
	return { getScreenReaderText };
};
